/**
 * Keyboard Navigation Hooks
 *
 * A collection of hooks for keyboard accessibility:
 * - useKeyboardShortcuts: Global keyboard shortcuts
 * - useFocusTrap: Focus trapping for modals/dialogs
 * - useArrowNavigation: Arrow key navigation for lists
 * - useRovingTabIndex: Roving tabindex pattern
 *
 * Usage:
 * useKeyboardShortcuts({
 *   'ctrl+k': () => openSearch(),
 *   'escape': () => closeModal(),
 * });
 */

import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Parse a keyboard shortcut string into modifiers and key
 */
const parseShortcut = (shortcut) => {
  const parts = shortcut.toLowerCase().split('+');
  const key = parts.pop();
  const modifiers = {
    ctrl: parts.includes('ctrl') || parts.includes('control'),
    meta: parts.includes('meta') || parts.includes('cmd') || parts.includes('command'),
    alt: parts.includes('alt') || parts.includes('option'),
    shift: parts.includes('shift'),
  };
  return { key, modifiers };
};

/**
 * Check if keyboard event matches a shortcut
 */
const matchesShortcut = (event, { key, modifiers }) => {
  const keyMatch = event.key.toLowerCase() === key;
  const ctrlMatch = modifiers.ctrl ? (event.ctrlKey || event.metaKey) : true;
  const metaMatch = modifiers.meta ? event.metaKey : true;
  const altMatch = modifiers.alt === event.altKey;
  const shiftMatch = modifiers.shift === event.shiftKey;

  return keyMatch && ctrlMatch && metaMatch && altMatch && shiftMatch;
};

/**
 * useKeyboardShortcuts - Global keyboard shortcuts
 *
 * @param {Object} shortcuts - Map of shortcut strings to handlers
 * @param {Object} options - Configuration options
 *
 * Usage:
 * useKeyboardShortcuts({
 *   'ctrl+k': () => openSearch(),
 *   'ctrl+n': () => createNew(),
 *   'escape': () => closeModal(),
 * });
 */
export const useKeyboardShortcuts = (shortcuts = {}, options = {}) => {
  const {
    enabled = true,
    preventDefault = true,
    stopPropagation = false,
    ignoreInputs = true,
  } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      // Ignore if focus is in an input (unless explicitly allowed)
      if (ignoreInputs) {
        const tagName = document.activeElement?.tagName?.toLowerCase();
        if (['input', 'textarea', 'select'].includes(tagName)) {
          // Still allow escape in inputs
          if (event.key !== 'Escape') return;
        }
      }

      // Check each shortcut
      for (const [shortcut, handler] of Object.entries(shortcuts)) {
        const parsed = parseShortcut(shortcut);
        if (matchesShortcut(event, parsed)) {
          if (preventDefault) event.preventDefault();
          if (stopPropagation) event.stopPropagation();
          handler(event);
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled, preventDefault, stopPropagation, ignoreInputs]);
};

/**
 * useFocusTrap - Trap focus within a container
 *
 * @param {boolean} isActive - Whether the trap is active
 * @param {Object} options - Configuration options
 * @returns {React.RefObject} - Ref to attach to the container
 *
 * Usage:
 * const modalRef = useFocusTrap(isOpen);
 * <div ref={modalRef}>...</div>
 */
export const useFocusTrap = (isActive = false, options = {}) => {
  const {
    initialFocus = 'first', // 'first', 'last', or a selector
    returnFocus = true,
    escapeDeactivates = true,
    onEscape,
  } = options;

  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    // Store previous focus
    previousActiveElement.current = document.activeElement;

    // Get focusable elements
    const getFocusableElements = () => {
      return containerRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      ) || [];
    };

    // Set initial focus
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      if (initialFocus === 'first') {
        focusableElements[0].focus();
      } else if (initialFocus === 'last') {
        focusableElements[focusableElements.length - 1].focus();
      } else if (typeof initialFocus === 'string') {
        const element = containerRef.current.querySelector(initialFocus);
        element?.focus();
      }
    }

    // Handle keyboard navigation
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && escapeDeactivates) {
        onEscape?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = getFocusableElements();
      if (elements.length === 0) return;

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: go backwards
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: go forwards
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Return focus
      if (returnFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, initialFocus, returnFocus, escapeDeactivates, onEscape]);

  return containerRef;
};

/**
 * useArrowNavigation - Arrow key navigation for lists
 *
 * @param {Array} items - Array of items to navigate
 * @param {Object} options - Configuration options
 * @returns {Object} - Current index, handlers, and refs
 *
 * Usage:
 * const { currentIndex, getItemProps, containerProps } = useArrowNavigation(items);
 */
export const useArrowNavigation = (items = [], options = {}) => {
  const {
    initialIndex = 0,
    loop = true,
    orientation = 'vertical', // 'vertical' | 'horizontal' | 'both'
    onSelect,
    onNavigate,
  } = options;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const itemRefs = useRef([]);

  // Ensure refs array is correct size
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  const navigate = useCallback(
    (direction) => {
      setCurrentIndex((prev) => {
        let next = prev + direction;

        if (loop) {
          if (next < 0) next = items.length - 1;
          if (next >= items.length) next = 0;
        } else {
          next = Math.max(0, Math.min(items.length - 1, next));
        }

        onNavigate?.(next, items[next]);
        return next;
      });
    },
    [items, loop, onNavigate]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const isVertical = orientation === 'vertical' || orientation === 'both';
      const isHorizontal = orientation === 'horizontal' || orientation === 'both';

      switch (event.key) {
        case 'ArrowUp':
          if (isVertical) {
            event.preventDefault();
            navigate(-1);
          }
          break;
        case 'ArrowDown':
          if (isVertical) {
            event.preventDefault();
            navigate(1);
          }
          break;
        case 'ArrowLeft':
          if (isHorizontal) {
            event.preventDefault();
            navigate(-1);
          }
          break;
        case 'ArrowRight':
          if (isHorizontal) {
            event.preventDefault();
            navigate(1);
          }
          break;
        case 'Home':
          event.preventDefault();
          setCurrentIndex(0);
          onNavigate?.(0, items[0]);
          break;
        case 'End':
          event.preventDefault();
          const lastIndex = items.length - 1;
          setCurrentIndex(lastIndex);
          onNavigate?.(lastIndex, items[lastIndex]);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelect?.(currentIndex, items[currentIndex]);
          break;
        default:
          break;
      }
    },
    [navigate, orientation, items, currentIndex, onSelect, onNavigate]
  );

  // Focus current item when index changes
  useEffect(() => {
    itemRefs.current[currentIndex]?.focus();
  }, [currentIndex]);

  const getItemProps = useCallback(
    (index) => ({
      ref: (el) => (itemRefs.current[index] = el),
      tabIndex: index === currentIndex ? 0 : -1,
      'aria-selected': index === currentIndex,
      onClick: () => {
        setCurrentIndex(index);
        onSelect?.(index, items[index]);
      },
    }),
    [currentIndex, items, onSelect]
  );

  const containerProps = {
    role: 'listbox',
    onKeyDown: handleKeyDown,
  };

  return {
    currentIndex,
    setCurrentIndex,
    getItemProps,
    containerProps,
    itemRefs,
  };
};

/**
 * useRovingTabIndex - Roving tabindex for widget patterns
 *
 * @param {number} itemCount - Number of items
 * @param {Object} options - Configuration options
 * @returns {Object} - Focus handlers and state
 */
export const useRovingTabIndex = (itemCount, options = {}) => {
  const { initialIndex = 0, loop = true } = options;
  const [focusedIndex, setFocusedIndex] = useState(initialIndex);

  const moveFocus = useCallback(
    (delta) => {
      setFocusedIndex((prev) => {
        let next = prev + delta;
        if (loop) {
          if (next < 0) next = itemCount - 1;
          if (next >= itemCount) next = 0;
        } else {
          next = Math.max(0, Math.min(itemCount - 1, next));
        }
        return next;
      });
    },
    [itemCount, loop]
  );

  const getTabIndex = useCallback(
    (index) => (index === focusedIndex ? 0 : -1),
    [focusedIndex]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    moveFocus,
    getTabIndex,
  };
};

/**
 * useSkipLink - Skip to main content link
 *
 * @param {string} targetId - ID of the main content element
 * @returns {Object} - Props for the skip link
 */
export const useSkipLink = (targetId = 'main-content') => {
  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      const target = document.getElementById(targetId);
      if (target) {
        target.tabIndex = -1;
        target.focus();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [targetId]
  );

  return {
    href: `#${targetId}`,
    onClick: handleClick,
    className: 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 ' +
               'focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white ' +
               'focus:rounded-lg focus:shadow-lg',
    children: 'Skip to main content',
  };
};

/**
 * Default keyboard shortcuts for the application
 */
export const defaultShortcuts = {
  search: 'ctrl+k',
  newAnalysis: 'ctrl+n',
  settings: 'ctrl+,',
  escape: 'escape',
  help: 'shift+?',
  goHome: 'g h',
  goProjects: 'g p',
  goSkills: 'g s',
};

/**
 * Export as default the main hook
 */
export default useKeyboardShortcuts;
